import { EVENTS_BY_SLUG, formatDateRangeLong } from '@/features/events';
import { Page } from '@/components/layout';
import { HeroMexico } from './sections/HeroMexico';
import { ManifiestoMexico } from './sections/ManifiestoMexico';
import { PilaresMexico } from './sections/PilaresMexico';
import { TriadaMexico } from './sections/TriadaMexico';
import { ProgramaMexico } from './sections/ProgramaMexico';
import { EntradasMexico } from './sections/EntradasMexico';
import { CierreMexico } from './sections/CierreMexico';
import { CancunUpsell } from '@/pages/shared/BriefBlocks';
import { CtaBand, PorQueIr, ReservaCta } from '@/pages/shared/ConversionBlocks';

/**
 * `/mexico` · Ciudad de México — calco de `DEMO_4_Mexico.png`.
 *
 * Las CINCO secciones del mockup, en su orden.
 *
 * OJO A LAS PROPORCIONES, QUE ESTE COMENTARIO LAS MENTÍA. Hasta el 26-jul-2026
 * aquí ponía «S1 43 % · S2 14 % · S3 10 % · S4 23 % · S5 10 %» como si fueran
 * las nuestras: son las DEL PÓSTER. Medidas sobre el render a 1024 (la pila del
 * calco, sin los dos bloques del ADR-006) las nuestras son:
 *
 *            S1     S2     S3     S4     S5
 *   póster   43,5 % 12,5 % 10,7 % 23,4 %  9,8 %
 *   render   31,5 % 17,2 %  9,4 % 30,0 % 11,8 %
 *
 * Un comentario que miente sobre una medida es peor que no tenerlo: éste dio por
 * calcada una proporción que nunca se comprobó.
 *
 * Y una lectura que hay que hacer entera, porque el número solo engaña: el hero
 * ACABA de arreglarse (ratio 1,450 contra el 1,533 del póster, recorte del
 * `cover` del 13,7 % al 3,3 %) y aun así su PORCENTAJE ha BAJADO del 36 % al
 * 31,5 %, alejándose del 43,5 % del póster. No es una regresión: es que al
 * encoger sólo el hero, las otras cuatro —que siguen infladas— pesan más en el
 * reparto. La proporción relativa no se arregla sección a sección; se arregla
 * cuando estén las cinco. S2, S3, S4 y S5 siguen pendientes.
 * Ver `docs/fidelidad/mexico.md`.
 *
 * Más DOS bloques que el mockup no dibuja y el brief exige (ADR-006):
 *   · `ProgramaMexico` — los cuatro bloques del fin de semana con sus horas.
 *     Sin esto se compran dos días a ciegas: es el hueco más grave que encontró
 *     el ORACLE en las seis páginas.
 *   · `CierreMexico` — prueba social real, FAQ, upsell al retiro de Cancún y
 *     enlace de vuelta a la gira. Quien llega por un anuncio a esta ciudad
 *     tenía la página sin salida hacia el resto del recorrido.
 *
 * Los dos van DESPUÉS del calco, no intercalados: el mockup se lee entero y
 * seguido, y lo que el brief añade viene detrás. Declarado en
 * `docs/VISUAL_DEVIATIONS.md` §1.12 y §1.13.
 */
export function MexicoCityPage() {
  const event = EVENTS_BY_SLUG.mexico;

  return (
    <Page
      documentTitle={event.city}
      description={`${event.title} · ${event.city}, ${formatDateRangeLong(event)}.`}
      tone={event.heroTone}
    >
      <HeroMexico event={event} />
      {/* La estructura que el cliente aprobó en `/buenos-aires` (28-jul-2026).
          Rompe el calco de `DEMO_4_Mexico.png` justo detrás del hero, que es
          precisamente lo que pidieron: «repetir la estructura en las diferentes
          ciudades, adaptando el texto». Declarado en `VISUAL_DEVIATIONS.md`. */}
      <ReservaCta event={event} />
      <ManifiestoMexico />
      <PilaresMexico event={event} />
      <TriadaMexico event={event} />
      {/* El calco termina AQUÍ, en su banda de cierre, como el póster. */}
      <EntradasMexico event={event} />
      {/* Y a partir de aquí lo que añade el brief (ADR-006), detrás y no
          intercalado. `ProgramaMexico` estaba metido ENTRE la tríada y
          `EntradasMexico`, partiendo el calco justo antes de su banda final y
          contradiciendo a este mismo docblock. Árbitro: el póster, que NO tiene
          banda de programa propia —su agenda de dos días vive dentro de la
          primera columna de la tríada, «UN VIAJE DE DOS DÍAS» con DÍA 1 y DÍA 2—,
          así que el bloque es una ADICIÓN y le toca ir detrás, donde ya estaba
          `CierreMexico`. */}
      <ProgramaMexico event={event} />
      {/* Banda de CTA justo detrás del programa y «por qué ir» a continuación:
          el mismo orden que en las otras cuatro. */}
      <CtaBand event={event} />
      <PorQueIr event={event} background="alt" />
      {/* El cierre del recorrido, en la forma compartida. Sale de dentro de
          `CierreMexico` —donde era una tarjeta centrada sin foto— y pasa a ser
          el mismo bloque que en las otras tres ciudades. */}
      <CancunUpsell desde={event} />
      <CierreMexico event={event} />
    </Page>
  );
}
